import React, { useState, useEffect, useRef, useMemo } from 'react';
import { authenticatedFetch } from './AuthComponent';
import { Timeline, DataSet } from 'vis-timeline/standalone';
import 'vis-timeline/styles/vis-timeline-graph2d.min.css';

// ── Colour palette ────────────────────────────────────────────────────────────
export const PORTAL_COLORS = {
    picking:  { bar: '#93c5fd', text: '#1e3a5f', label: 'Picking'  },
    pressing: { bar: '#fdba74', text: '#7c2d12', label: 'Pressing' },
    packing:  { bar: '#86efac', text: '#14532d', label: 'Packing'  },
    print:    { bar: '#c4b5fd', text: '#3b0764', label: 'Print'    },
    orders:   { bar: '#d8b4fe', text: '#581c87', label: 'Pickup'   },
    // Synthetic events emitted by the CLOSED-FO cascade (recache_stale.py
    // lift_item_state_from_closed_fos). Distinct teal so it's visually
    // separate from user-attributable 'admin' actions.
    auto:     { bar: '#a5f3fc', text: '#155e75', label: 'Auto-completed' },
    admin:    { bar: '#d1d5db', text: '#374151', label: 'Admin'    },
    refund:   { bar: '#fecaca', text: '#991b1b', label: 'Refund'   },
    issue:    { bar: '#dc2626', text: '#991b1b', label: 'Issue Reported' },
};

export function portalColor(portal) {
    return PORTAL_COLORS[portal] || PORTAL_COLORS.admin;
}

// Map an event_type to its workflow domain. Used to give admin-portal sessions
// (Pressing Tools → Mark Picked/Pressed/Packed) the same color and label as a
// real PIN-portal session, so the timeline reader can see the workflow
// context. Without this, admin actions render as a generic gray "ADMIN" card.
export function eventDomain(eventType) {
    if (!eventType) return null;
    switch (eventType) {
        case 'pick': case 'pick_unmark': case 'pick_complete':
        case 'mark_all_picked': case 'mark_selected_picked':
        case 'mark_selected_not_picked': case 'picking_reset':
            return 'picking';
        case 'press_manual': case 'press_unmark': case 'bundle_finish':
        case 'bundle_unpress': case 'mark_all_pressed':
        case 'mark_selected_pressed': case 'mark_selected_not_pressed':
        case 'pressing_reset':
            return 'pressing';
        case 'pack_pass': case 'pack_fail': case 'pack_unmark':
        case 'packing_reset': case 'shipping_label_created':
            return 'packing';
        case 'refund_issued':
            return 'refund';
        case 'issue_reported':
            return 'issue';
        default:
            return null;
    }
}

// For an admin-portal session, compute its dominant domain from member events.
// Returns null if events span multiple domains or no domain at all.
// Also applied to admin_console sessions so refund_issued events can inherit
// their refund color rather than falling back to generic gray.
export function sessionDomain(session) {
    if (!session || (session.portal !== 'admin' && session.portal !== 'admin_console')) return null;
    const domains = new Set();
    for (const ev of (session.events || [])) {
        const d = eventDomain(ev.event_type);
        if (d) domains.add(d);
    }
    return domains.size === 1 ? [...domains][0] : null;
}

// Split a picker/press group_key into its three semantic parts:
//   `SKU`              → { base: "SKU", player: null, number: null }
//   `SKU!Player Name`  → { base: "SKU", player: "Player Name", number: null }
//   `SKU#23`           → { base: "SKU", player: null, number: "23" }
//   `SKU!Player#23`    → { base: "SKU", player: "Player", number: "23" }
// The picking portal builds these split-keys per player so item_state and
// events track each player's copy of the same SKU separately. The user
// shouldn't see the raw suffix — render it as small chips next to the SKU.
// Pretty-print a group_key for display. Strips the internal __no_sku__<gid>
// placeholder (the picker writer uses it as a stable key for embellishment
// line items that have no SKU) so users see "item" instead of a raw
// `__no_sku__gid://shopify/LineItem/16345...` string.
export function formatGroupKey(gk, fallback = 'item') {
    if (!gk) return fallback;
    const s = String(gk);
    if (s.startsWith('__no_sku__')) return fallback;
    return s;
}

export function parseGroupKey(gk) {
    if (!gk) return { base: '', player: null, number: null };
    const s = String(gk);
    const playerIdx = s.indexOf('!');
    const numberIdx = s.indexOf('#');
    let base = s, player = null, number = null;
    if (playerIdx >= 0 && (numberIdx < 0 || playerIdx < numberIdx)) {
        base   = s.slice(0, playerIdx);
        const rest = s.slice(playerIdx + 1);
        const hashAfter = rest.indexOf('#');
        if (hashAfter >= 0) {
            player = rest.slice(0, hashAfter) || null;
            number = rest.slice(hashAfter + 1) || null;
        } else {
            player = rest || null;
        }
    } else if (numberIdx >= 0) {
        base   = s.slice(0, numberIdx);
        const rest = s.slice(numberIdx + 1);
        const bangAfter = rest.indexOf('!');
        if (bangAfter >= 0) {
            number = rest.slice(0, bangAfter) || null;
            player = rest.slice(bangAfter + 1) || null;
        } else {
            number = rest || null;
        }
    }
    // Strip the lingering `|x` legacy marker if present on the base.
    const pipeIdx = base.indexOf('|');
    if (pipeIdx >= 0) base = base.slice(0, pipeIdx);
    return { base, player, number };
}

export function eventLabel(ev) {
    const gk = formatGroupKey(ev.group_key);
    const gkOrBundle = formatGroupKey(ev.group_key, 'bundle');
    const gkOrItems  = formatGroupKey(ev.group_key, 'items');
    switch (ev.event_type) {
        case 'pick': {
            const m = ev.payload?.method || 'manual';
            return `${m === 'scan' ? 'Scanned' : 'Manually picked'}: ${gk}`;
        }
        case 'pick_scan':             return `Scanned: ${gk}`;
        case 'pick_manual':           return `Manually picked: ${gk}`;
        case 'pick_complete':         return 'Order Picking Completed';
        case 'pick_save':             return `Picked: ${gkOrItems}`;
        case 'bundle_finish':         return `Pressed: ${gkOrBundle}`;
        case 'bundle_skip':           return `Skipped: ${gkOrBundle}`;
        case 'order_finish':          return 'Order Finished (Pressing)';
        case 'order_print':           return ev.payload?.merged_nj_warehouses ? 'Order Printed (NJ warehouses merged)' : 'Order Printed';
        case 'order_reprint':         return `Order Reprinted${ev.payload?.full_order ? ' (full order)' : ''}${ev.payload?.merged_nj_warehouses ? ' (NJ warehouses merged)' : ''}`;
        case 'shipping_label_created': {
            const price = ev.payload?.price;
            return `Shipping Label Created${price ? ` ($${price})` : ''}`;
        }
        case 'mark_all_picked':       return 'Marked All As Picked';
        case 'mark_all_pressed':      return 'Marked All As Pressed';
        case 'mark_selected_picked':  return 'Marked Selected As Picked';
        case 'mark_selected_not_picked':  return 'Marked Selected As Not Picked';
        case 'mark_selected_pressed': return 'Marked Selected As Pressed';
        case 'mark_selected_not_pressed': return 'Marked Selected As Not Pressed';
        case 'pick_unmark':           return gk;
        case 'press_manual':          return gk;
        case 'press_unmark':          return gk;
        case 'picking_reset':         return 'Picking History Reset';
        case 'pressing_reset':        return 'Pressing History Reset';
        case 'pack_scan':    return `Scanned: ${gk}`;
        case 'pack_manual':  return `Manual QC: ${gk}`;
        case 'pack_pass':    return gk;
        case 'pack_fail': {
            const reason = (ev.payload?.reason) || '';
            const action = (ev.payload?.action) || '';
            const note = (ev.payload?.note) || '';
            const noteSfx = note ? ` · Note: "${note}"` : '';
            return `${gk}${reason ? ` — ${reason}` : ''}${action === 'rework' ? ' (Rework)' : action === 'partial' ? ' (Partial)' : ''}${noteSfx}`;
        }
        case 'pack_complete': {
            const isPartial = ev.payload?.partial;
            return isPartial ? 'Partial Fulfillment Created' : 'Fulfillment Created';
        }
        case 'pack_resume':   return 'Packing Resumed';
        case 'packing_reset': return 'Packing History Reset';
        case 'issue_reported': {
            const reason = ev.payload?.reason || '';
            const note = ev.payload?.note ? ` · Note: "${ev.payload.note}"` : '';
            return `Issue Reported${reason ? ` — ${reason}` : ''}${note}`;
        }
        case 'store_transfer': {
            const loc = ev.payload?.to_location_name;
            return loc ? `Transferred to ${loc}` : 'Transferred to pickup location';
        }
        case 'fulfillment_cancelled': {
            const p = ev.payload || {};
            const bits = [];
            if (p.label_voided) bits.push(`label voided${p.void_reason ? ` (${p.void_reason.toLowerCase().replace(/_/g, ' ')})` : ''}`);
            if (p.reverted) bits.push(`moved back${p.move_inventory ? ', inventory moved' : ', inventory not moved'}`);
            return `Fulfillment Cancelled${bits.length ? ` — ${bits.join(' · ')}` : ''}`;
        }
        case 'bulk_label_assigned': {
            const t = ev.payload?.tracking_number; const loc = ev.payload?.to_location_name;
            return `Bulk Shipment Label Assigned${t ? ` — ${t}` : ''}${loc ? ` (to ${loc})` : ''}`;
        }
        case 'bulk_label_removed': {
            const t = ev.payload?.tracking_number;
            return `Bulk Shipment Label Removed${t ? ` — ${t}` : ''}`;
        }
        case 'bulk_box_shipped': {
            const t = ev.payload?.tracking_number; const loc = ev.payload?.to_location_name;
            return `Bulk Box Shipped${t ? ` — ${t}` : ''}${loc ? ` (to ${loc})` : ''}`;
        }
        case 'bulk_box_reopened': {
            const t = ev.payload?.tracking_number;
            return `Bulk Box Reopened${t ? ` — ${t}` : ''}`;
        }
        case 'bulk_box_redirected': {
            const t = ev.payload?.tracking_number; const a = ev.payload?.from_store; const b = ev.payload?.to_store;
            return `Bulk Box Destination Changed${t ? ` — ${t}` : ''}${a && b ? ` (${a} → ${b})` : (b ? ` (to ${b})` : '')}`;
        }
        case 'ready_for_pickup': return 'Marked as Ready for Pickup';
        case 'store_scan_received': return `Scanned in at store — Shopify could not mark it ready for pickup${ev.payload?.reason ? ` (${String(ev.payload.reason).slice(0, 90)})` : ''}`;
        case 'picked_up':        return 'Marked as Picked Up';
        case 'pickup_override_set': {
            const newLoc = ev.payload?.new || '';
            const prevLoc = ev.payload?.prev || '';
            return prevLoc
                ? `Pickup location → ${newLoc} (was ${prevLoc})`
                : `Pickup location set: ${newLoc}`;
        }
        case 'pickup_override_cleared': {
            const prevLoc = ev.payload?.prev || '';
            return prevLoc
                ? `Pickup override cleared (was ${prevLoc})`
                : 'Pickup override cleared';
        }
        case 'refund_issued': {
            const cents = ev.payload?.total_refunded_cents || 0;
            const amt = (cents / 100).toFixed(2);
            const dest = ev.payload?.destination === 'store_credit' ? 'store credit' : 'original payment';
            return `Refunded $${amt} to ${dest}`;
        }
        default: return (ev.event_type || 'event').replace(/_/g, ' ');
    }
}

// Auto-cascade events are emitted per group_key (one pick_complete + one
// pack_pass per touched row, sometimes more if pressing was lifted too).
// Rendering them as raw rows produces N duplicate "Order Picking Completed"
// lines that look like the same event repeated. This helper collapses an
// auto session's events into one row per group_key with combined verb chips,
// matching the format the operator actually cares about: "what got lifted?"
//
// When `allEvents` is provided, auto chips are SUPPRESSED on a per-verb basis
// whenever the same base SKU already has a corresponding manual event in the
// full event log. Rationale: an auto pick_complete fired after the picker
// already manually marked the SKU adds noise — the picker did the work, the
// cascade just confirmed it. Same for press / pack. If an entry has all
// three verbs suppressed it is dropped entirely; callers can detect that
// case (empty return) to hide the auto card itself.
//
// Returns: [{ group_key, picked, pressed, packed, sample_ev }] — one entry
// per unique group_key, in first-seen order.
const _AUTO_BASE_RE = /[!#|].*$/;
const _stripSplit = (gk) => (gk || '').replace(_AUTO_BASE_RE, '');

export function consolidateAutoEvents(events, allEvents) {
    // Manual coverage: base_sku → { picked, pressed, packed }. Built from
    // every non-auto event in `allEvents` (default = events itself, which
    // preserves the old behavior when callers don't pass the full log).
    const cover = new Map();
    const fullList = Array.isArray(allEvents) ? allEvents : events;
    for (const ev of fullList) {
        if ((ev.portal || '') === 'auto') continue;
        const base = _stripSplit(ev.group_key);
        if (!base) continue;
        const t = ev.event_type;
        const c = cover.get(base) || { picked: false, pressed: false, packed: false };
        // Pick verbs: explicit scans/manual picks and the unmark counterpart
        // (the unmark is itself the picker's deliberate action).
        if (t === 'pick' || t === 'pick_scan' || t === 'pick_manual' || t === 'pick_unmark') c.picked = true;
        // Press verbs: pressing portal explicit actions.
        if (t === 'bundle_finish' || t === 'bundle_skip' || t === 'press_manual'
            || t === 'press_unmark' || t === 'bundle_unfinish' || t === 'order_finish') c.pressed = true;
        // Pack verbs: QC pass / fail / reset.
        if (t === 'pack_pass' || t === 'pack_fail' || t === 'pack_unmark') c.packed = true;
        cover.set(base, c);
    }

    const map = new Map();
    for (const ev of events) {
        const gk = ev.group_key || '';
        if (!gk) continue;
        if (!map.has(gk)) {
            map.set(gk, { group_key: gk, picked: false, pressed: false, packed: false, sample_ev: ev });
        }
        const row = map.get(gk);
        if (ev.event_type === 'pick_complete') row.picked = true;
        else if (ev.event_type === 'bundle_finish' || ev.event_type === 'press_manual') row.pressed = true;
        else if (ev.event_type === 'pack_pass') row.packed = true;
    }

    // Per-row suppression of chips already covered by a manual event on the
    // same base SKU. Drop rows where every chip is suppressed so the card
    // doesn't render empty rows for fully-manual orders.
    const out = [];
    for (const row of map.values()) {
        const base = _stripSplit(row.group_key);
        const c = cover.get(base);
        if (c) {
            if (c.picked)  row.picked  = false;
            if (c.pressed) row.pressed = false;
            if (c.packed)  row.packed  = false;
        }
        if (row.picked || row.pressed || row.packed) out.push(row);
    }
    return out;
}

// Filter predicate: press→pick cascade events.  When a presser scans a bundle
// the backend writes a `bundle_finish` (the real press) AND a `pick` with
// reason='press_auto' (auto-crediting the picker).  The cascade pick must stay
// in the DB (recompute_portal_pct / reports read it), but it should NOT appear
// in the timeline — it breaks consecutive-run collapse and shows a misleading
// "Manual" row alongside every press row.
export function isCascadePressAutoPick(ev) {
    return (ev.event_type === 'pick' || ev.event_type === 'pick_complete')
        && ev.payload?.reason === 'press_auto';
}

// Filter out session boundary events (pressing_session_start/end, picking_session_start/end,
// packing_session_start/end) from per-session render. These rows break the consecutive-collapse
// chain of work events. The session card header already encodes the boundary times, so the
// rows inside the body are redundant. Filter at render layer; leave events in DB for reports.
const _SESSION_BOUNDARY_TYPES = new Set([
    'pressing_session_start', 'pressing_session_end',
    'picking_session_start',  'picking_session_end',
    'packing_session_start',  'packing_session_end',
]);
export function isSessionBoundary(ev) {
    return _SESSION_BOUNDARY_TYPES.has(ev?.event_type);
}

// Filter predicate: pressing/packing events on bundle-child SKUs.
// Embellishment children of a bundle (NECK1, STHL-TUS4-WHT, etc. relative to
// a garment parent like ADID-JF6064-S) are pressed AS the parent (heat-
// applied onto the shirt) and packed AS the parent (already on the shirt at
// pack time). Any pressing/packing entry on a child is either a cascade
// artifact or wrong — hide it; the parent's row represents the whole bundle.
// PICKING-PORTAL events on children stay visible: at pick time the picker
// physically retrieves each embellishment (name/number/patch) as a separate
// item from the bin, so their pick action is real work worth showing.
// The check is on `portal='picking'`, NOT on event_type: qc_cascade emits
// `pick` events under `portal='packing'` and those must stay hidden even
// though the event_type is a picker verb.
//
// Two ways to identify a child:
//   1. In the order's bundle_membership_json values (explicit bundle child).
//   2. `product_type` is one of Logo/Number/Name/Crest/Decals (embellishment).
//      Used as a fallback because bundle_membership_json misses standalone
//      embellishments (customer bought a name-only add-on) — but the same
//      physical rule still applies: at pack time the emb is on the shirt.
//      Bug: SP-255433 STHL-PROBLOCK1-WHT rendered auto pack_pass rows
//      because it isn't in bundle_membership_json but IS a Number emb.
// Pass a Set of child base SKUs derived from bundle_membership_json values,
// and the itemsMap from /api/orders/{id}/timeline for product_type lookup.
const _EMB_PRODUCT_TYPES = new Set(['Logo', 'Number', 'Name', 'Crest', 'Decals']);
export function isBundleChildEvent(ev, bundleChildBases, itemsMap) {
    const base = _stripSplit(ev?.group_key);
    if (!base) return false;
    const inBundleChild = bundleChildBases && bundleChildBases.size > 0 && bundleChildBases.has(base);
    const item = itemsMap ? (itemsMap[base] || itemsMap[ev?.group_key]) : null;
    const isEmb = !!(item && item.product_type && _EMB_PRODUCT_TYPES.has(item.product_type));
    if (!inBundleChild && !isEmb) return false;
    // Keep picking-portal events visible even on children.
    if ((ev?.portal || '') === 'picking') return false;
    return true;
}

// Derive the Set of child base SKUs from the /timeline endpoint's
// bundle_membership payload ({parent_base: [child_base, ...]}). Idempotent
// against missing/malformed input.
export function bundleChildSet(bundleMembership) {
    const out = new Set();
    if (!bundleMembership || typeof bundleMembership !== 'object') return out;
    for (const children of Object.values(bundleMembership)) {
        if (!Array.isArray(children)) continue;
        for (const c of children) if (c) out.add(c);
    }
    return out;
}

// Build a flat list of sessions across all users.
// Each session carries the user's displayName so the card can show "PICKING: Eric Linenberg".
export const GAP_MS = 30 * 60 * 1000;

export function buildSessions(events) {
    const byUser = {};
    events.forEach(ev => {
        const key         = ev.portal_username || 'System';
        const displayName = ev.display_name || key;
        const evPortal    = ev.portal || 'admin';
        const isPrint     = evPortal === 'print';
        if (!byUser[key]) byUser[key] = { displayName, sessions: [] };
        const ts       = new Date(ev.created_at).getTime();
        const sessions = byUser[key].sessions;
        const last     = sessions[sessions.length - 1];
        if (!last || ts - last.endTs > GAP_MS || (!isPrint && evPortal !== last.portal)) {
            sessions.push({ portal: evPortal, startTs: ts, endTs: ts, events: [ev] });
        } else {
            last.endTs = ts;
            last.events.push(ev);
        }
    });
    // Flatten: sort users so System comes first, then alphabetical
    return Object.entries(byUser)
        .sort(([a], [b]) => a === 'System' ? -1 : b === 'System' ? 1 : a.localeCompare(b))
        .flatMap(([, { displayName, sessions }]) =>
            sessions.map(s => ({ ...s, displayName }))
        );
}

function fmtDate(ts) {
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtTime(ts) {
    return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// ── Build item as a real DOM Element to bypass vis-timeline's XSS sanitizer ──
// vis-timeline strips class/style from HTML strings but appends Element objects as-is.
function sessionElement(session, itemsMap, allEvents) {
    // Admin-portal sessions: surface the workflow domain (picking/pressing/
    // packing) inferred from the events themselves, so the timeline reader
    // can see whether the admin marked items picked vs pressed vs packed.
    // Without this, admin actions render as a generic gray "ADMIN" card with
    // no workflow context. Fall back to admin styling when the events span
    // multiple domains or none.
    const adminDomain = sessionDomain(session);
    const col      = adminDomain ? portalColor(adminDomain) : portalColor(session.portal);
    const dateStr  = fmtDate(session.startTs);
    const timeStr  = session.startTs === session.endTs
        ? fmtTime(session.startTs)
        : `${fmtTime(session.startTs)} – ${fmtTime(session.endTs)}`;

    // Outer card
    const card = document.createElement('div');
    Object.assign(card.style, {
        border:       `1.5px solid ${col.bar}`,
        borderRadius: '6px',
        background:   '#fff',
        boxShadow:    '0 2px 6px rgba(0,0,0,.12)',
        overflow:     'hidden',
        minWidth:     '240px',
        cursor:       'default',
        userSelect:   'none',
    });

    // Colored header
    const header = document.createElement('div');
    Object.assign(header.style, {
        background:     col.bar,
        color:          col.text,
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'space-between',
        padding:        '4px 9px',
        gap:            '10px',
    });

    const portalLbl = document.createElement('span');
    Object.assign(portalLbl.style, {
        fontSize:      '10px',
        fontWeight:    '700',
        letterSpacing: '.05em',
        textTransform: 'uppercase',
        whiteSpace:    'nowrap',
    });
    // Auto-cascade sessions get a friendlier header — no displayName slot
    // (these aren't tied to a user) and the label spells out what happened.
    // Admin-portal sessions inherit the workflow domain's label but get an
    // "(ADMIN)" suffix so the reader knows it came from the Pressing Tools
    // dropdown, not a real picker/presser/packer kiosk session.
    // QC-cascade sessions (packing portal, portal_username=NULL, every event
    // carries payload.reason='qc_cascade') get labeled "System · QC Cascade"
    // so the reader can tell at a glance the item was auto-marked pressed
    // because the packer QC-passed the bundle — no human presser touched it.
    const _isQcCascadeSession = session.portal === 'packing'
        && (session.displayName === 'System' || !session.displayName)
        && (session.events || []).length > 0
        && (session.events || []).every(e => e?.payload?.reason === 'qc_cascade');
    if (session.portal === 'auto') {
        portalLbl.textContent = 'AUTO-COMPLETED ON FULFILLMENT';
    } else if (_isQcCascadeSession) {
        portalLbl.textContent = 'SYSTEM · QC CASCADE';
    } else {
        const baseLabel = adminDomain
            ? `${col.label.toUpperCase()} (ADMIN)`
            : col.label.toUpperCase();
        portalLbl.textContent = session.displayName
            ? `${baseLabel}: ${session.displayName}`
            : baseLabel;
    }

    const timeLbl = document.createElement('span');
    Object.assign(timeLbl.style, {
        fontSize:   '10px',
        opacity:    '.72',
        whiteSpace: 'nowrap',
    });
    timeLbl.textContent = `${dateStr} · ${timeStr}`;

    header.appendChild(portalLbl);
    header.appendChild(timeLbl);
    card.appendChild(header);

    // Events list
    const eventsWrap = document.createElement('div');
    eventsWrap.style.background = '#fff';
    eventsWrap.style.padding = '2px 0';

    // Auto-cascade sessions render ONE row per group_key with combined chips
    // (Picked + Packed) instead of one row per raw event. Bails out of the
    // standard per-event renderer below.
    if (session.portal === 'auto') {
        const consolidated = consolidateAutoEvents(session.events, allEvents);
        // Fully-suppressed auto session — every chip was shadowed by manual
        // events. Return null so the caller can drop the timeline item
        // entirely instead of leaving a header-only "Auto-completed on
        // fulfillment" card with no content.
        if (!consolidated.length) return null;
        consolidated.forEach((entry, idx) => {
            const row = document.createElement('div');
            Object.assign(row.style, {
                display:    'flex',
                alignItems: 'flex-start',
                gap:        '5px',
                padding:    '3px 9px',
                fontSize:   '11px',
                color:      '#202223',
                borderTop:  idx === 0 ? 'none' : '1px solid rgba(0,0,0,.05)',
                lineHeight: '1.45',
                flexWrap:   'wrap',
            });
            const chips = [];
            if (entry.picked)  chips.push({ text: 'Picked',  bg: '#bbf7d0', color: '#166534' });
            if (entry.pressed) chips.push({ text: 'Pressed', bg: '#fed7aa', color: '#9a3412' });
            if (entry.packed)  chips.push({ text: 'Packed',  bg: '#bbf7d0', color: '#166534' });
            chips.forEach(({ text, bg, color }) => {
                const b = document.createElement('span');
                Object.assign(b.style, {
                    fontSize:     '10px',
                    fontWeight:   '600',
                    padding:      '1px 5px',
                    borderRadius: '10px',
                    background:   bg,
                    color:        color,
                    flexShrink:   '0',
                    whiteSpace:   'nowrap',
                    marginTop:    '1px',
                });
                b.textContent = text;
                row.appendChild(b);
            });
            const txt = document.createElement('span');
            txt.style.wordBreak = 'break-word';
            const item = itemsMap[entry.group_key];
            const parsed = parseGroupKey(entry.group_key);
            txt.textContent = item?.title ? `${item.title} / ${parsed.base}` : (parsed.base || '');
            row.appendChild(txt);
            if (parsed.number) {
                const num = document.createElement('span');
                num.textContent = `#${parsed.number}`;
                num.style.cssText = 'margin-left:6px;padding:1px 6px;border-radius:4px;background:#dbeafe;color:#1e40af;font-size:11px;font-weight:600;';
                row.appendChild(num);
            }
            if (parsed.player) {
                const ply = document.createElement('span');
                ply.textContent = parsed.player;
                ply.style.cssText = 'margin-left:6px;padding:1px 6px;border-radius:4px;background:#fce7f3;color:#9d174d;font-size:11px;font-weight:600;';
                row.appendChild(ply);
            }
            eventsWrap.appendChild(row);
        });
        card.appendChild(eventsWrap);
        return card;
    }

    // Admin bulk reset: when a session is composed entirely of *_unmark
    // events of the same type (operator clicked "Reset all" on a portal),
    // collapse into a single summary row. Per-item rows are noise — what
    // matters is "the operator wiped the picked state on this order".
    const allUnmarkType = (() => {
        const evs = session.events || [];
        if (evs.length < 3) return null;  // small resets stay per-row for context
        const types = new Set(evs.map(e => e.event_type));
        if (types.size !== 1) return null;
        const t = [...types][0];
        if (!['pick_unmark', 'press_unmark', 'pack_unmark'].includes(t)) return null;
        return t;
    })();
    if (allUnmarkType) {
        const labels = {
            pick_unmark:  { verb: 'Unpicked',  bg: '#fecaca', color: '#991b1b' },
            press_unmark: { verb: 'Unpressed', bg: '#fecaca', color: '#991b1b' },
            pack_unmark:  { verb: 'Reset',     bg: '#fecaca', color: '#991b1b' },
        };
        const { verb, bg, color } = labels[allUnmarkType];
        const distinctKeys = new Set(session.events.map(e => e.group_key).filter(Boolean));
        const row = document.createElement('div');
        Object.assign(row.style, {
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '4px 9px', fontSize: '11px', color: '#202223',
        });
        const badge = document.createElement('span');
        Object.assign(badge.style, {
            fontSize: '10px', fontWeight: '600', padding: '1px 5px',
            borderRadius: '10px', background: bg, color, whiteSpace: 'nowrap',
        });
        badge.textContent = verb;
        row.appendChild(badge);
        const txt = document.createElement('span');
        txt.textContent = `All items reset (${distinctKeys.size} item${distinctKeys.size === 1 ? '' : 's'})`;
        row.appendChild(txt);
        eventsWrap.appendChild(row);
        card.appendChild(eventsWrap);
        return card;
    }

    // Collapse consecutive events for the same (event_type, group_key) into a
    // single row with a "×N" multiplier. The packing portal logs one pack_pass
    // event per QC pass — if the customer ordered 3 of the same SKU and the
    // packer passes them one by one, the timeline used to show 3 identical
    // rows. Same problem for press events on multi-instance bundles. Keep the
    // raw events in the DB; just group at render time. (SP-249804 + the long
    // SP-... orders were the canonical repros.)
    const COLLAPSE_TYPES = new Set([
        'pack_pass', 'pack_fail', 'bundle_finish', 'press_manual',
        'pick', 'pick_scan', 'pick_manual',
    ]);
    const groupedEvents = (() => {
        const out = [];
        for (const ev of session.events.filter(e => !isCascadePressAutoPick(e) && !isSessionBoundary(e))) {
            const last = out[out.length - 1];
            if (last
                && last.ev.event_type === ev.event_type
                && last.ev.group_key === ev.group_key
                && COLLAPSE_TYPES.has(ev.event_type)) {
                last.count += 1;
                last.lastTs = ev.created_at;
                continue;
            }
            out.push({ ev, count: 1, lastTs: ev.created_at });
        }
        return out;
    })();

    groupedEvents.forEach(({ ev, count }, idx) => {
        const row = document.createElement('div');
        const _isIssue = ev.event_type === 'issue_reported';
        Object.assign(row.style, {
            display:    'flex',
            alignItems: 'flex-start',
            gap:        '5px',
            padding:    _isIssue ? '4px 9px 4px 6px' : '2px 9px',
            fontSize:   '11px',
            color:      _isIssue ? '#991b1b' : '#202223',
            borderTop:  idx === 0 ? 'none' : '1px solid rgba(0,0,0,.05)',
            lineHeight: '1.45',
            // 2026-07-10: Report Issue events render with a red pastel
            // background + subtle left border so CS escalations are visible
            // at a glance in the timeline. Matches the pack_fail Failed
            // badge palette.
            ...(_isIssue ? {
                background: '#fecaca',
                borderLeft: '3px solid #dc2626',
                fontWeight: '500',
            } : {}),
        });

        // Scan / Manual / Unpicked / Unpressed badge — shows HOW the item was
        // marked (scanned vs tapped). pack_fail handled below since Failed
        // takes precedence over the method.
        if (['pick','pick_scan','pick_manual','press_manual','bundle_finish','pick_unmark','press_unmark','pack_pass'].includes(ev.event_type)) {
            const evMethod = ev.payload?.method;
            const isScan     = ev.event_type === 'pick_scan'
                            || ((ev.event_type === 'pick'
                                 || ev.event_type === 'bundle_finish'
                                 || ev.event_type === 'pack_pass') && evMethod === 'scan');
            const isUnpicked = ev.event_type === 'pick_unmark';
            const isUnpressed = ev.event_type === 'press_unmark';
            const badge = document.createElement('span');
            Object.assign(badge.style, {
                fontSize:    '10px',
                fontWeight:  '600',
                padding:     '1px 5px',
                borderRadius:'10px',
                background:   isScan ? '#bbf7d0' : (isUnpicked || isUnpressed) ? '#fecaca' : '#fed7aa',
                color:        isScan ? '#166534' : (isUnpicked || isUnpressed) ? '#991b1b' : '#9a3412',
                flexShrink:  '0',
                whiteSpace:  'nowrap',
                marginTop:   '1px',
            });
            badge.textContent = isScan ? 'Scan'
                              : isUnpicked ? 'Unpicked'
                              : isUnpressed ? 'Unpressed'
                              : 'Manual';
            row.appendChild(badge);

            // Manager Override attribution — surfaced as a second badge
            // right after Manual when the event payload carries the
            // manager identity that authorized the scan-only bypass.
            const mgrOv = ev.payload?.manager_override;
            if (mgrOv && (mgrOv.name || mgrOv.first_name)) {
                const mgrName = mgrOv.name
                    || `${mgrOv.first_name || ''} ${mgrOv.last_name || ''}`.trim();
                const mgrBadge = document.createElement('span');
                Object.assign(mgrBadge.style, {
                    fontSize:    '10px',
                    fontWeight:  '700',
                    padding:     '1px 6px',
                    borderRadius:'10px',
                    background:  '#dcfce7',
                    color:       '#166534',
                    border:      '1px solid #86efac',
                    flexShrink:  '0',
                    whiteSpace:  'nowrap',
                    marginTop:   '1px',
                    marginLeft:  '4px',
                });
                mgrBadge.textContent = `Manager Override — ${mgrName}`;
                row.appendChild(mgrBadge);
            }
        }

        // Failed badge (pack_fail keeps the explicit failure indicator over
        // method, since the failure state is more salient than scan vs tap).
        if (ev.event_type === 'pack_fail') {
            const badge = document.createElement('span');
            Object.assign(badge.style, {
                fontSize:    '10px',
                fontWeight:  '600',
                padding:     '1px 5px',
                borderRadius:'10px',
                background:   '#fecaca',
                color:        '#991b1b',
                flexShrink:  '0',
                whiteSpace:  'nowrap',
                marginTop:   '1px',
            });
            badge.textContent = 'Failed';
            row.appendChild(badge);
        }

        const txt = document.createElement('span');
        txt.style.wordBreak = 'break-word';

        // Render the SKU label cleanly (Title / BASE) and append small chips
        // for the !Player and #Number suffixes so they don't bleed into the
        // SKU itself.
        const appendChips = (parsed) => {
            if (parsed.number) {
                const num = document.createElement('span');
                num.textContent = `#${parsed.number}`;
                num.style.cssText = 'margin-left:6px;padding:1px 6px;border-radius:4px;background:#dbeafe;color:#1e40af;font-size:11px;font-weight:600;';
                row.appendChild(num);
            }
            if (parsed.player) {
                const ply = document.createElement('span');
                ply.textContent = parsed.player;
                ply.style.cssText = 'margin-left:6px;padding:1px 6px;border-radius:4px;background:#fce7f3;color:#9d174d;font-size:11px;font-weight:600;';
                row.appendChild(ply);
            }
        };

        if (['pick','pick_scan','pick_manual','press_manual','bundle_finish','pick_unmark','press_unmark'].includes(ev.event_type)) {
            const item = itemsMap[ev.group_key];
            const parsed = parseGroupKey(ev.group_key);
            const skuLabel = (item && item.title) ? `${item.title} / ${parsed.base}` : (parsed.base || '');
            // Prefer the collapse count (multiple events fired one per
            // instance) over qty_after (single event with embedded qty)
            const qty  = Math.max(count, Number(ev.qty_after) || 1);
            txt.textContent = (qty > 1) ? `${skuLabel} ×${qty}` : skuLabel;
            row.appendChild(txt);
            appendChips(parsed);
        } else if (ev.event_type === 'pack_pass' || ev.event_type === 'pack_fail') {
            const item = itemsMap[ev.group_key];
            const parsed = parseGroupKey(ev.group_key);
            const skuLabel = (item && item.title) ? `${item.title} / ${parsed.base}` : (parsed.base || '');
            // Pack QC pass fires ONE event for a qty=N SKU (single action
            // lifts qty_packed=qty_total in one shot). So fall back to
            // item_state.qty_total when the collapse count is 1.
            const packQty = Math.max(count, Number(item?.qty_total) || 1);
            const suffix = packQty > 1 ? ` ×${packQty}` : '';
            if (ev.event_type === 'pack_fail') {
                const reason = ev.payload?.reason ? ` — ${ev.payload.reason}` : '';
                const action = ev.payload?.action === 'rework' ? ' (Rework)' : ev.payload?.action === 'partial' ? ' (Partial)' : '';
                const note   = ev.payload?.note ? ` · Note: "${ev.payload.note}"` : '';
                txt.textContent = `${skuLabel}${suffix}${reason}${action}${note}`;
            } else {
                txt.textContent = `${skuLabel}${suffix}`;
            }
            row.appendChild(txt);
            appendChips(parsed);
        } else {
            txt.textContent = eventLabel(ev);
            row.appendChild(txt);
        }
        eventsWrap.appendChild(row);
    });

    card.appendChild(eventsWrap);
    return card;
}

// ── Anchor card (Order Placed / Fulfill By) ─────────────────────────────────
// Always-visible markers shown on the timeline regardless of event activity.
function anchorElement({ label, ts, accent, accentText }) {
    const dateStr = fmtDate(ts);
    const timeStr = fmtTime(ts);
    const card = document.createElement('div');
    Object.assign(card.style, {
        border:       `1.5px solid ${accent}`,
        borderRadius: '6px',
        background:   '#fff',
        boxShadow:    '0 2px 6px rgba(0,0,0,.12)',
        overflow:     'hidden',
        minWidth:     '200px',
        cursor:       'default',
        userSelect:   'none',
    });
    const header = document.createElement('div');
    Object.assign(header.style, {
        background:     accent,
        color:          accentText,
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'space-between',
        padding:        '4px 9px',
        gap:            '10px',
    });
    const lbl = document.createElement('span');
    Object.assign(lbl.style, { fontSize: '10px', fontWeight: '700', letterSpacing: '.05em', textTransform: 'uppercase' });
    lbl.textContent = label;
    const time = document.createElement('span');
    Object.assign(time.style, { fontSize: '10px', opacity: '.72', whiteSpace: 'nowrap' });
    time.textContent = `${dateStr} · ${timeStr}`;
    header.appendChild(lbl);
    header.appendChild(time);
    card.appendChild(header);
    return card;
}

// ── Main component ────────────────────────────────────────────────────────────
const TWELVE_HOURS = 12 * 60 * 60 * 1000;

export default function OrderTimeline({ orderId, order, fulfillBy, onClose, initialViewStart, initialViewEnd, fetchFn, embedded }) {
    const containerRef = useRef(null);
    const tlRef        = useRef(null);

    const [events,   setEvents]   = useState([]);
    const [itemsMap, setItemsMap] = useState({});
    const [bundleMembership, setBundleMembership] = useState({});
    const [loading,  setLoading]  = useState(true);

    const orderStartMs = useMemo(
        () => new Date(order?.processedAt || order?.createdAt).getTime(),
        [order]
    );
    const fulfillByMs = useMemo(
        () => fulfillBy ? new Date(fulfillBy).getTime() : 0,
        [fulfillBy]
    );

    const defaultStart = useRef(null);
    const defaultEnd   = useRef(null);
    if (defaultStart.current === null) {
        defaultStart.current = initialViewStart ?? (orderStartMs - TWELVE_HOURS);
        defaultEnd.current   = initialViewEnd   ?? (Date.now()  + TWELVE_HOURS);
    }

    // ── Fetch events ──────────────────────────────────────────────────────────
    useEffect(() => {
        (fetchFn || authenticatedFetch)(`/api/orders/${orderId}/timeline`)
            .then(r => r.ok ? r.json() : { events: [], items: {}, bundle_membership: {} })
            .then(d => {
                setEvents(d.events || []);
                setItemsMap(d.items || {});
                setBundleMembership(d.bundle_membership || {});
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [orderId]);

    // Memoized set of child base SKUs for the bundle-child event filter.
    // Rebuilt only when bundle_membership changes (usually once per fetch).
    const bundleChildBases = useMemo(
        () => bundleChildSet(bundleMembership),
        [bundleMembership]
    );

    // ── Build / refresh vis-timeline ──────────────────────────────────────────
    useEffect(() => {
        if (loading || !containerRef.current) return;

        // Hide embellishment-child rows before building sessions. The bundle
        // parent's row represents the whole bundle in the timeline; per-child
        // lifts are internal accounting. Keeps events in DB for reports and
        // drift monitor; filter is render-only.
        // Filter always runs (even when bundleChildBases is empty) so the
        // product_type=EMB fallback still catches standalone embellishments.
        const visibleEvents = events.filter(e => !isBundleChildEvent(e, bundleChildBases, itemsMap));
        const sessions = buildSessions(visibleEvents);

        // Items — DOM elements bypass the XSS sanitizer entirely; no groups = single track.
        const MIN_VISUAL = 2 * 60 * 1000;
        const sessionItems = sessions
            .map((s, i) => {
                const content = sessionElement(s, itemsMap, visibleEvents);
                if (!content) return null;  // fully-suppressed auto session
                return {
                    id:        `s${i}`,
                    start:     new Date(s.startTs),
                    end:       new Date(Math.max(s.endTs, s.startTs + MIN_VISUAL)),
                    content,
                    title:     '',
                    editable:  false,
                    className: 'vt-item',
                };
            })
            .filter(Boolean);
        const anchorItems = [];
        if (orderStartMs && isFinite(orderStartMs)) {
            anchorItems.push({
                id:    'anchor-placed',
                start: new Date(orderStartMs),
                end:   new Date(orderStartMs + MIN_VISUAL),
                content: anchorElement({ label: 'Order Placed', ts: orderStartMs, accent: '#0d2240', accentText: '#fff' }),
                title: '', editable: false, className: 'vt-item',
            });
        }
        if (fulfillByMs && isFinite(fulfillByMs)) {
            anchorItems.push({
                id:    'anchor-fulfillby',
                start: new Date(fulfillByMs),
                end:   new Date(fulfillByMs + MIN_VISUAL),
                content: anchorElement({ label: 'Fulfill By', ts: fulfillByMs, accent: '#7c2d12', accentText: '#fff' }),
                title: '', editable: false, className: 'vt-item',
            });
        }
        const items = new DataSet([...anchorItems, ...sessionItems]);

        const options = {
            start:           new Date(defaultStart.current),
            end:             new Date(defaultEnd.current),
            zoomMin:         60 * 1000,
            zoomMax:         365 * 24 * 3600 * 1000,
            showCurrentTime: true,
            stack:           true,
            orientation:     { axis: 'top', item: 'top' },
            autoResize:      true,
            selectable:      false,
            moveable:        true,
            zoomable:        true,
            height:          '100%',
        };

        // Destroy prior instance
        if (tlRef.current) {
            tlRef.current.destroy();
            tlRef.current = null;
        }

        const tl = new Timeline(containerRef.current, items, options);
        tlRef.current = tl;

        // Add custom time lines (Order Created, Fulfill By)
        try {
            if (orderStartMs && isFinite(orderStartMs)) tl.addCustomTime(new Date(orderStartMs), 'order-created');
            if (fulfillByMs  && isFinite(fulfillByMs))  tl.addCustomTime(new Date(fulfillByMs),  'fulfill-by');
        } catch (_) { /* best-effort */ }

        // Inject visible data-label attributes after vis-timeline finishes async init.
        // The CSS ::before reads attr(data-label) — the outer .vis-custom-time element is
        // moved but never recreated on zoom/pan, so the attribute persists.
        const containerEl = containerRef.current;
        const labelTimer = setTimeout(() => {
            const set = (cls, text) => {
                const el = containerEl?.querySelector(`.vis-custom-time.${cls}`);
                if (el) el.dataset.label = text;
            };
            set('order-created', 'Order Created');
            set('fulfill-by',    'Fulfill By');
        }, 150);

        // Sticky cards: when an item bar scrolls off the left edge, translate
        // the inner card so it stays visible inside the remaining bar width.
        // Persist view state in URL
        // Edge fade is handled purely by CSS mask on .vis-itemset (no JS needed).
        tl.on('rangechanged', ({ start, end }) => {
            const params = new URLSearchParams(window.location.search);
            params.set('view', 'timeline');
            params.set('vs', Math.round(start.getTime()).toString());
            params.set('ve', Math.round(end.getTime()).toString());
            window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
        });

        return () => {
            clearTimeout(labelTimer);
            tl.destroy();
            tlRef.current = null;
        };
    }, [events, itemsMap, loading, orderStartMs, fulfillByMs, bundleChildBases]);

    // ── Zoom controls ─────────────────────────────────────────────────────────
    const zoomIn  = () => tlRef.current?.zoomIn(0.5);
    const zoomOut = () => tlRef.current?.zoomOut(0.5);
    const reset   = () => tlRef.current?.setWindow(
        new Date(orderStartMs - TWELVE_HOURS),
        new Date(Date.now() + TWELVE_HOURS),
        { animation: { duration: 400, easingFunction: 'easeInOutQuad' } }
    );

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className={`tl-overlay ${embedded ? 'tl-overlay--embedded' : ''}`}>
            {/* Header — hidden when embedded inside V2 (order name + dates */}
            {/* are already in the V2 hero pills) */}
            {!embedded && (
                <div className="tl-header">
                    <button className="tl-back" onClick={onClose}>← Back to Order</button>
                    <div className="tl-title-block">
                        <h2 className="tl-title">Timeline — {order?.name}</h2>
                        <div className="tl-subtitle">
                            <span>Placed {new Date(orderStartMs).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                            {fulfillByMs > 0 && (
                                <span>Fulfill by {new Date(fulfillByMs).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                            )}
                        </div>
                    </div>
                    <div className="tl-zoom-controls">
                        <button className="tl-zoom-btn" onClick={zoomIn}>＋</button>
                        <button className="tl-zoom-btn" onClick={zoomOut}>－</button>
                        <button className="tl-zoom-btn tl-zoom-btn--reset" onClick={reset}>Reset</button>
                    </div>
                </div>
            )}
            {embedded && (
                <div className="tl-zoom-floating">
                    <button className="tl-zoom-btn" onClick={zoomIn}>＋</button>
                    <button className="tl-zoom-btn" onClick={zoomOut}>－</button>
                    <button className="tl-zoom-btn tl-zoom-btn--reset" onClick={reset}>Reset</button>
                </div>
            )}

            {/* Body */}
            {loading ? (
                <div className="tl-loading">Loading timeline…</div>
            ) : (
                <>
                    {events.length === 0 && (
                        <div className="tl-empty-note">No portal activity yet — showing order milestones.</div>
                    )}
                    <div className="tl-vis-wrap">
                        <div ref={containerRef} className="tl-vis-container" />
                        <div className="tl-edge-fade tl-edge-fade--left"  />
                        <div className="tl-edge-fade tl-edge-fade--right" />
                    </div>
                    {/* Legend */}
                    <div className="tl-legend">
                        {Object.entries(PORTAL_COLORS).map(([k, c]) => (
                            <span key={k} className="tl-legend-item">
                                <span className="tl-legend-dot" style={{ background: c.bar }} />
                                {c.label}
                            </span>
                        ))}
                        <span className="tl-legend-item">
                            <span className="tl-legend-dot" style={{ background: '#ef4444', borderRadius: 2 }} />
                            Now
                        </span>
                        <span className="tl-legend-item">
                            <span className="tl-legend-dot" style={{ background: '#0d2240', borderRadius: 2 }} />
                            Order Placed
                        </span>
                        <span className="tl-legend-item">
                            <span className="tl-legend-dot" style={{ background: '#7c2d12', borderRadius: 2 }} />
                            Fulfill By
                        </span>
                    </div>
                    <div className="tl-hint">Scroll to zoom · Drag to pan</div>
                </>
            )}
        </div>
    );
}
